/*
 * -- Extenal Imports -- 
 */
import { Box, Link, List, ListItem, Typography, styled } from "@mui/material";
import { FC } from "react";

/*
 * -- Internal Imports -- 
 */
import Image from '../common/Image';
import { AppSettings } from "../../AppSettings";

// -- Page elements
import FacebookIcon from '../../assets/images/feral-scan/page-elements/facebook_header.png';
import SubscribeIcon from '../../assets/images/feral-scan/page-elements/icon_subscribe_white.png';
import TwitterIcon from '../../assets/images/feral-scan/page-elements/twitter_header.png';
import YoutubeIcon from '../../assets/images/feral-scan/page-elements/youtube_header.png';

// -- Sponsors --
import Banksia from '../../assets/images/feral-scan/sponsors/Banksia.small.jpg';
import DepartmentOfPrimaryIndustries from '../../assets/images/feral-scan/sponsors/IandINSW.png';
import AusGov from '../../assets/images/feral-scan/sponsors/daff.jpg';
import LocalLandServices from '../../assets/images/feral-scan/sponsors/localland.small.png';
import AWI from '../../assets/images/feral-scan/sponsors/logo-awi.png';
import InvasiveSpeciesSolutions from '../../assets/images/feral-scan/sponsors/logo-ciss.svg';
import PetSmart from '../../assets/images/feral-scan/sponsors/pestsmart-logo.png';
import Center from '../common/Center';
import FooterContainer from '../common/Footer';
import { useParams } from "react-router-dom";

/*
 * -- Components -- 
 */
const BaseSocialMediaFooter = styled(Box)(() => ({
    padding: '8px 0',
}));

const HomePageFooter = styled(Box)(({ theme }) => ({
    background: theme.palette.common.white,
    position: 'relative',
    backgroundColor: 'white',
    padding: '0 10px',
    zIndex: 8
}));

const HomePageFooterSponsors = styled(Box)(() => ({
    position: 'relative',
    backgroundColor: 'white',
    margin: 0,
    listStyleType: 'none',
    alignItems: 'center',
    flexDirection: 'column',
    textAlign: 'center',
    display: 'flex',
    
    "@media (min-width: 1300px)": {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center'
    }
}));

const HomePageFooterSponsorsWrapper = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    width: '100%',

    "@media (min-width: 1300px)": {
        alignItems: 'center'
    }
}));

const HomePageFooterSponsor = styled(Box)(() => ({
    height: '60%',
    padding: '12px 24px',
    minWidth: '200px',
    width: "auto",

    "img": {
        maxWidth: '100%',
        display: 'inline',
        height: '100%',
        maxHeight: '60px'
    },

    "@media (min-width: 600px)": {
        minWidth: '200px',
        width: 'auto'
    }
}));

const StyledHomePageLink = styled(Link)(() => ({
    textDecoration: "none",
    color: "white",
    fontWeight: 700,

    "&:hover": {
        cursor: "pointer",
        textDecoration: "none",
        color: "white",
    },
    
    "& p": {
        color: "white"
    },

    "&:hover p": {
        color: "#ff6600",
    }
}));

const HomePageFooterIcon = styled(Image)(() => ({
    opacity: '0.5',
    maxWidth: '30px',
    minWidth: '30px',
    display: 'inline',
    maxHeight: '30px',
    minHeight: '30px',
    color: 'red',

    "&:hover": {
        opacity: "1",
        cursor: 'pointer'
    }
}));

const Footer: FC = () => {

    const { species: speciesName } = useParams();

    const dateNow = new Date();
    
    return (
        <>
            {/* -- Footer -- */}
            <HomePageFooter borderTop='1px solid #e6e6e6'>
                <Box display="block" position="relative">
                    <HomePageFooterSponsors>
                        <HomePageFooterSponsorsWrapper>
                            <HomePageFooterSponsor>
                                <Link
                                    target="_blank"
                                    href="https://invasives.com.au/"
                                    sx={{
                                        '&:hover': {
                                            opacity: '0.65'
                                        }
                                    }}
                                >
                                    <Image src={InvasiveSpeciesSolutions}/>
                                </Link>
                            </HomePageFooterSponsor>
                            <HomePageFooterSponsor>
                                <Link>
                                    <Image src={PetSmart}/>
                                </Link>
                            </HomePageFooterSponsor>
                            <HomePageFooterSponsor>
                                <Link>
                                    <Image src={DepartmentOfPrimaryIndustries}/>
                                </Link>
                            </HomePageFooterSponsor>
                            <HomePageFooterSponsor>
                                <Link>
                                    <Image src={AWI}/>
                                </Link>
                            </HomePageFooterSponsor>
                            <HomePageFooterSponsor>
                                <Link>
                                    <Image src={AusGov}/>
                                </Link>
                            </HomePageFooterSponsor>
                            <HomePageFooterSponsor>
                                <Link>
                                    <Image src={LocalLandServices}/>
                                </Link>
                            </HomePageFooterSponsor>
                            <HomePageFooterSponsor>
                                <Link>
                                    <Image src={Banksia} />
                                </Link>
                            </HomePageFooterSponsor>
                        </HomePageFooterSponsorsWrapper>
                    </HomePageFooterSponsors>
                </Box>
            </HomePageFooter>
            <FooterContainer>
                <BaseSocialMediaFooter>
                    <Center ha='end' mb='5px'>
                        <StyledHomePageLink href="https://www.pestsmart.org.au/privacy/">
                            <Typography 
                                noWrap 
                                fontSize="0.9rem"
                                fontWeight="600"
                                display="inline"
                            >
                                Privacy Statement
                            </Typography>
                        </StyledHomePageLink>
                        <Typography fontSize="0.9rem" color="white" display="inline">&nbsp; Copyright © 2009-{dateNow.getFullYear()} Invasive Animals Ltd. Technology by </Typography><Typography color="white" fontSize="0.9rem"  display="inline" fontWeight="bold">&nbsp;NewtonGreen</Typography>
                    </Center>
                    <Center ha='end' mb='5px'>
                        <StyledHomePageLink href={speciesName ? `/${speciesName}/content/${speciesName?.replace("scan", "")?.replace("alert", "")}_contacts` : `mailto:${AppSettings.systemMailAddress}`}>
                            <Typography 
                                noWrap 
                                whiteSpace="none"
                                fontWeight="600"
                            >
                                Contact Us
                            </Typography>
                        </StyledHomePageLink>
                    </Center>
                    <Center ha='end' mb='5px'>
                        <List
                            sx={{
                                margin: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                textAlign: 'center',

                                "@media (min-width: 800px)": {
                                    padding: 0,
                                    paddingLeft: '120px',
                                    justifyContent: 'flex-start',
                                }
                            }}
                        >
                            <ListItem sx={{ margin: "0 0 0 16px", padding: 0 }}>
                                <Link 
                                    href="https://www.facebook.com/CentreInvasiveSpecies/"
                                    target="_blank"
                                >
                                    <HomePageFooterIcon src={FacebookIcon}/>
                                </Link>
                            </ListItem>
                            <ListItem sx={{ margin: "0 0 0 16px", padding: 0 }}>
                                <Link
                                    href="https://x.com/centreinvasives"
                                    target="_blank"
                                >
                                    <HomePageFooterIcon src={TwitterIcon}/>
                                </Link>
                            </ListItem>
                            <ListItem sx={{ margin: "0 0 0 16px", padding: 0 }}>
                                <Link
                                    href="https://www.youtube.com/c/CentreforInvasiveSpeciesSolutions"
                                    target="_blank"
                                >
                                    <HomePageFooterIcon src={YoutubeIcon}/>
                                </Link>
                            </ListItem>
                            <ListItem sx={{ margin: "0 0 0 16px", padding: 0 }}>
                                <Link
                                    href="https://pestsmart.org.au/subscribe-to-our-newsletter/"
                                    target="_blank"
                                >
                                    <HomePageFooterIcon src={SubscribeIcon}/>
                                </Link>
                            </ListItem>
                        </List>
                    </Center>
                </BaseSocialMediaFooter>
            </FooterContainer>
        </>
    )
}

export default Footer;